import React, { useState } from "react";
import {
    Box,
    Card,
    CardMedia,
    CardActions,
    IconButton,
    Typography,
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    Chip,
    Tooltip,
    CircularProgress,
} from "@mui/material";
import { Delete, Star, StarBorder, ZoomIn } from "@mui/icons-material";
import { ProductImage } from "../../types/productImage";

interface ProductImageGalleryProps {
    images: ProductImage[];
    onDelete: (imageId: string) => Promise<void>;
    onSetDefault: (imageId: string) => Promise<void>;
    loading?: boolean;
}

export const ProductImageGallery: React.FC<ProductImageGalleryProps> = ({
    images,
    onDelete,
    onSetDefault,
    loading = false,
}) => {
    const [selectedImage, setSelectedImage] = useState<ProductImage | null>(
        null,
    );
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
    const [processing, setProcessing] = useState<string | null>(null);

    const handleDelete = async (imageId: string) => {
        setProcessing(imageId);
        try {
            await onDelete(imageId);
            setDeleteConfirm(null);
        } catch (error) {
            console.error("Failed to delete image:", error);
        } finally {
            setProcessing(null);
        }
    };

    const handleSetDefault = async (imageId: string) => {
        setProcessing(imageId);
        try {
            await onSetDefault(imageId);
        } catch (error) {
            console.error("Failed to set default image:", error);
        } finally {
            setProcessing(null);
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (images.length === 0) {
        return (
            <Box
                sx={{
                    p: 4,
                    textAlign: "center",
                    border: "1px dashed",
                    borderColor: "grey.300",
                    borderRadius: 1,
                }}
            >
                <Typography color="text.secondary">
                    No images uploaded yet
                </Typography>
            </Box>
        );
    }

    const sortedImages = [...images].sort(
        (a, b) => a.display_order - b.display_order,
    );

    return (
        <>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                {sortedImages.map((image) => (
                    <Box
                        key={image.image_id}
                        sx={{
                            width: {
                                xs: "100%",
                                sm: "calc(50% - 8px)",
                                md: "calc(33.333% - 11px)",
                                lg: "calc(25% - 12px)",
                            },
                        }}
                    >
                        <Card
                            sx={{
                                position: "relative",
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            {image.is_default && (
                                <Chip
                                    label="Default"
                                    color="primary"
                                    size="small"
                                    sx={{
                                        position: "absolute",
                                        top: 8,
                                        left: 8,
                                        zIndex: 1,
                                    }}
                                />
                            )}
                            <CardMedia
                                component="img"
                                height="200"
                                image={image.medium_url}
                                alt={image.alt_text || "Product image"}
                                sx={{
                                    objectFit: "cover",
                                    cursor: "pointer",
                                }}
                                onClick={() => setSelectedImage(image)}
                            />
                            <Box sx={{ p: 1, flexGrow: 1 }}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    noWrap
                                >
                                    {image.original_filename}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    display="block"
                                    color="text.secondary"
                                >
                                    {image.width} × {image.height} •{" "}
                                    {formatFileSize(image.file_size)}
                                </Typography>
                            </Box>
                            <CardActions
                                sx={{ justifyContent: "space-between", pt: 0 }}
                            >
                                <Tooltip
                                    title={
                                        image.is_default
                                            ? "Default image"
                                            : "Set as default"
                                    }
                                >
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                handleSetDefault(image.image_id)
                                            }
                                            disabled={
                                                image.is_default ||
                                                processing === image.image_id
                                            }
                                            color={
                                                image.is_default
                                                    ? "primary"
                                                    : "default"
                                            }
                                        >
                                            {image.is_default ? (
                                                <Star />
                                            ) : (
                                                <StarBorder />
                                            )}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Box>
                                    <Tooltip title="View full size">
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                setSelectedImage(image)
                                            }
                                        >
                                            <ZoomIn />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete image">
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() =>
                                                setDeleteConfirm(image.image_id)
                                            }
                                            disabled={
                                                processing === image.image_id
                                            }
                                        >
                                            <Delete />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </CardActions>
                        </Card>
                    </Box>
                ))}
            </Box>

            {/* Full size image dialog */}
            <Dialog
                open={selectedImage !== null}
                onClose={() => setSelectedImage(null)}
                maxWidth="lg"
                fullWidth
            >
                {selectedImage && (
                    <>
                        <DialogContent>
                            <Box sx={{ textAlign: "center" }}>
                                <img
                                    src={selectedImage.large_url}
                                    alt={
                                        selectedImage.alt_text ||
                                        "Product image"
                                    }
                                    style={{
                                        maxWidth: "100%",
                                        maxHeight: "70vh",
                                        objectFit: "contain",
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mt: 2 }}
                                >
                                    {selectedImage.original_filename} •{" "}
                                    {selectedImage.width} ×{" "}
                                    {selectedImage.height} •{" "}
                                    {formatFileSize(selectedImage.file_size)}
                                </Typography>
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setSelectedImage(null)}>
                                Close
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Delete confirmation dialog */}
            <Dialog
                open={deleteConfirm !== null}
                onClose={() => setDeleteConfirm(null)}
            >
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this image? This action
                        cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm(null)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() =>
                            deleteConfirm && handleDelete(deleteConfirm)
                        }
                        color="error"
                        variant="contained"
                        disabled={processing !== null}
                    >
                        {processing === deleteConfirm
                            ? "Deleting..."
                            : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
